// src/payment/components/StatusModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  LuCircleCheck, 
  LuCircleX, 
  LuCircleAlert, 
  LuTrophy, 
  LuRefreshCw,
  LuSparkles,
  LuArrowRight
} from 'react-icons/lu';
import './StatusModal.css';

const StatusModal = ({ 
  status: initialStatus = 'success', 
  message: initialMessage, 
  onClose, 
  redirectToCompetitors,
  errorCode 
}) => {
  // 🧪 MODO DESARROLLO: Permite alternar entre Éxito, Error y Advertencia para maquetar
  const isDev = process.env.NODE_ENV === 'development';
  const [devStatus, setDevStatus] = useState(initialStatus);

  // Usamos el estado del dev o el real si estamos en producción
  const status = isDev ? devStatus : initialStatus;

  // Mensaje por defecto enriquecido según el estado si no viene uno custom
  const message = initialMessage || (
    status === 'success' 
      ? '¡Tu pago ha sido verificado correctamente! Tu inscripción ya está confirmada para el evento.' 
      : status === 'error'
      ? 'No pudimos validar la transacción con el banco. Asegúrate de haber realizado la transferencia e intenta nuevamente.'
      : 'Hay un detalle con tu solicitud que requiere atención antes de continuar.'
  );

  const handleButtonClick = () => {
    if (status === 'success' && redirectToCompetitors) {
      redirectToCompetitors();
    } else {
      onClose();
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'success':
        return (
          <div className="status-icon-wrapper success">
            <div className="icon-glow" />
            <LuCircleCheck className="main-icon" />
            <LuSparkles className="sparkle-icon left" />
            <LuSparkles className="sparkle-icon right" />
          </div>
        );
      case 'error':
        return (
          <div className="status-icon-wrapper error">
            <div className="icon-glow" />
            <LuCircleX className="main-icon" />
          </div>
        );
      case 'warning':
        return (
          <div className="status-icon-wrapper warning">
            <div className="icon-glow" />
            <LuCircleAlert className="main-icon" />
          </div>
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return '¡Inscripción Exitosa!';
      case 'error':
        return 'Error en el Pago';
      case 'warning':
        return '¡Atención Requerida!';
      default:
        return 'Estado del Pago';
    }
  };

  const getButtonText = () => {
    switch (status) {
      case 'success':
        return (
          <>
            <LuTrophy className="btn-icon" /> Ver Lista de Participantes <LuArrowRight className="btn-arrow" />
          </>
        );
      case 'error':
        return (
          <>
            <LuRefreshCw className="btn-icon" /> Reintentar Validación
          </>
        );
      case 'warning':
        return 'Entendido, Continuar';
      default:
        return 'Cerrar';
    }
  };

  return (
    <div className="status-modal-overlay">
      <div className={`status-modal-card ${status}`}>

        {/* 🧪 CONTROLES DE DESARROLLO (Solo visibles en npm start) */}
        {isDev && (
          <div className="dev-status-bar">
            <span>DEV MODE:</span>
            <button 
              type="button" 
              className={`dev-btn ${devStatus === 'success' ? 'active' : ''}`}
              onClick={() => setDevStatus('success')}
            >
              Éxito
            </button>
            <button 
              type="button" 
              className={`dev-btn ${devStatus === 'error' ? 'active' : ''}`}
              onClick={() => setDevStatus('error')}
            >
              Error
            </button>
            <button 
              type="button" 
              className={`dev-btn ${devStatus === 'warning' ? 'active' : ''}`}
              onClick={() => setDevStatus('warning')}
            >
              Warning
            </button>
          </div>
        )}

        {/* Halo neón decorativo superior */}
        <div className={`status-top-glow ${status}`} />

        {/* Icono animado */}
        <div className="status-icon-container">
          {getIcon()}
        </div>

        {/* Encabezado y títulos */}
        <div className="status-text-content">
          <span className={`status-badge ${status}`}>
            {status === 'success' ? 'PROCESADO' : status === 'error' ? 'FALLIDO' : 'ALERTA'}
          </span>

          <h2 className={`status-title ${status}`}>
            {getTitle()}
          </h2>

          {errorCode && (
            <div className="status-error-code">
              Código de error: <code>{errorCode}</code>
            </div>
          )}

          <p className="status-message">{message}</p>
        </div>

        {/* Acción Principal */}
        <div className="status-actions">
          <button 
            className={`status-button ${status}`} 
            onClick={handleButtonClick}
            type="button"
          >
            {getButtonText()}
          </button>
        </div>

      </div>
    </div>
  );
};

StatusModal.propTypes = {
  status: PropTypes.oneOf(['success', 'error', 'warning']),
  message: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  redirectToCompetitors: PropTypes.func,
  errorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default StatusModal;